import apiClient from './api';

// Every staff table operation goes through apiClient, since several endpoints require the JWT.

// ==================== TABLE OPERATIONS ====================
export const getAllTables = async (restaurantId, filters = {}) => {
  try {
    // Build JSON body for POST request
    const body = {};
    
    // Active status filter
    if (filters.activeOnly !== undefined) {
      body.isActive = filters.activeOnly;
    } else if (filters.isActive !== undefined) {
      body.isActive = filters.isActive;
    }
    
    // Floor filter
    if (filters.floorId) {
      body.floor = parseInt(filters.floorId);
    } else if (filters.floor) {
      body.floor = parseInt(filters.floor);
    }
    
    // Room filter
    if (filters.roomId) {
      body.room = parseInt(filters.roomId);
    } else if (filters.room) {
      body.room = parseInt(filters.room);
    }
    
    // Guest/capacity filter
    if (filters.minCapacity) {
      body.guests = parseInt(filters.minCapacity);
    } else if (filters.guests) {
      body.guests = parseInt(filters.guests);
    } else if (filters.pax) {
      body.guests = parseInt(filters.pax);
    }
    
    // Date/time filter for availability
    if (filters.asOf) {
      body.date = filters.asOf;
    } else if (filters.date) {
      body.date = filters.date;
    }
    
    
    const response = await apiClient.post(
      `/users/restaurant/${restaurantId}/getAllTables`,
      body
    );
    
    return response.data; // Returns { success, restaurantId, filters, tables: [...], count }
  } catch (error) {
    throw error.response?.data || error.message;
  }
};



export const searchAvailability = async (restaurantId, params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    // Required
    if (params.startAt) queryParams.append('startAt', params.startAt);
    if (params.endAt) queryParams.append('endAt', params.endAt);
    if (params.pax) queryParams.append('pax', params.pax);
    
    // Optional
    if (params.allowJoin !== undefined) queryParams.append('allowJoin', params.allowJoin);
    if (params.allowShare !== undefined) queryParams.append('allowShare', params.allowShare);
    if (params.allowOverride !== undefined) queryParams.append('allowOverride', params.allowOverride);
    if (params.roomId) queryParams.append('roomId', params.roomId);
    
    const response = await apiClient.get(
      `/users/restaurant/${restaurantId}/tables/availability/?${queryParams}`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

/* The first of the booking contract's two calls: file the guest into the register, best-effort, before the booking. */
export const ensureCustomer = async ({ name, phone, title }) => {
  if (!phone || !String(phone).trim()) return null;
  try {
    const salutation =
      { mr: 'MR', mrs: 'MRS', ms: 'MS' }[String(title || '').toLowerCase()] || '';
    const response = await apiClient.post('/customers/admin/customers/ensure/', {
      phone: String(phone).trim(),
      name: String(name || '').trim(),
      salutation,
    });
    return response.data?.success ? response.data.data : null;
  } catch {
    return null;
  }
};

export const bookTable = async (restaurantId, tableId, bookingData) => {
  try {
    // Call 1 of 2 — the guest register (create-if-missing, keyed on phone).
    await ensureCustomer({
      name: bookingData.customer_name,
      phone: bookingData.customer_phone,
      title: bookingData.customer_title,
    });
    // Call 2 of 2 — the booking itself.
    const response = await apiClient.post(
      `/users/restaurant/${restaurantId}/tables/${tableId}/book/`,
      bookingData
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// PHONE VERIFICATION for staff bookings: request a code, check it, and send the token with the booking.

// The branch's booking policy — read to know whether a code is needed at all.
export const getReservationSettings = async (restaurantId, locationId) => {
  try {
    const response = await apiClient.get(
      `/users/restaurant/${restaurantId}/reservation-settings/`,
      { params: locationId ? { location: locationId } : {} }
    );
    return response.data?.data || {};
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const requestReservationOtp = async (restaurantId, phone, channel) => {
  try {
    const response = await apiClient.post(
      `/users/restaurant/${restaurantId}/reservations/otp/request/`,
      { phone, channel }
    );
    if (!response.data?.success) throw new Error(response.data?.error || 'Could not send the code');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Returns the single-use token the booking carries as proof.
export const verifyReservationOtp = async (restaurantId, phone, code, channel) => {
  try {
    const response = await apiClient.post(
      `/users/restaurant/${restaurantId}/reservations/otp/verify/`,
      { phone, code, channel }
    );
    if (!response.data?.success) throw new Error(response.data?.error || 'That code is not valid');
    return response.data.otp_token;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const guestArrived = async (restaurantId, reservationId) => {
  try {
    const response = await apiClient.post(
      `/users/restaurant/${restaurantId}/reservations/${reservationId}/arrived/`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const closeSession = async (restaurantId, sessionId) => {
  try {
    const response = await apiClient.post(
      `/users/restaurant/${restaurantId}/sessions/${sessionId}/close/`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Close every open session on a table to free it; cancelOrders also cancels its unserved orders.
export const freeTable = async (restaurantId, tableId, { cancelOrders = false } = {}) => {
  try {
    const response = await apiClient.post(
      `/users/restaurant/${restaurantId}/tables/${tableId}/free/`,
      { cancel_orders: cancelOrders }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getTableHistory = async (restaurantId, tableId) => {
  try {
    const response = await apiClient.get(
      `/users/restaurant/${restaurantId}/tables/${tableId}/history/`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

